import Alert from '@amzn/awsui-components-react/polaris/alert';
import Header from '@amzn/awsui-components-react/polaris/header';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Spinner from '@amzn/awsui-components-react/polaris/spinner';
import { TableProps } from '@amzn/awsui-components-react/polaris/table';
import {
  Event,
  GetAttendancesByQueryParamCommandOutput,
  GetShiftsByEventCommandOutput,
  GetSignupsByQueryParamCommandOutput,
  ShiftType,
} from '@amzn/red-velvet-api';
import { useLocalizedText } from '../../hooks/localizedText';
import { useSearchParams } from 'react-router-dom';
import { useAllPagesRedVelvetQuery } from '../../hooks/allPagesRedVelvetQuery';
import { useSignupAlerts } from '../../hooks/shifts/shiftSignup';
import { useGetAllWaitlistsByUser, useWaitlistAlerts } from '../../hooks/shifts/shiftWaitlist';
import { useUser } from '../../hooks/user';
import { VolunteerEventShiftsTable } from '../shifts/volunteerEventShiftsTable';
import { SignupSurface } from '../../utils/constants';

/** Rows per page on the Event Details page — generous since it is the primary signup surface. */
const EVENT_PAGE_SHIFT_TABLE_PAGE_SIZE = 50;

/**
 * Event Details wrapper for VolunteerEventShiftsTable. Loads the event's shifts and the current
 * user's signups + attendances using the same queries as ShiftCarousel (so the React Query cache is
 * shared). Unlike the carousel it does NOT hide deadline-passed / full / past shifts — those are
 * exactly what the table surfaces (Deadline Past status, Join Waitlist, Track Time). It still hides
 * the non-volunteer-facing shifts: WAITLIST (overflow backing shifts) and DROP_IN (coordinator
 * walk-in), plus the legacy 'Waitlist'-named GIVEHUB shift (cce-3510 mitigation).
 *
 * waitlistEntries are the user's waitlist entries for this event, fetched via useGetAllWaitlistsByUser
 * (cce-7112) and scoped to event.eventId below; ACTIVE ones resolve downstream to the ON_THE_WAITLIST
 * status (getUserStatus) and the Leave action (getShiftAction), so that state now renders here too.
 */
export function EventVolunteerShiftTable({
  event,
  pageSize = EVENT_PAGE_SHIFT_TABLE_PAGE_SIZE,
  pageSizeOptions,
  showTableTitle = true,
  tableVariant,
  source,
}: {
  event: Event;
  pageSize?: number;
  pageSizeOptions?: number[];
  showTableTitle?: boolean;
  tableVariant?: TableProps.Variant;
  source?: SignupSurface;
}) {
  const { t } = useLocalizedText(['translation']);
  const user = useUser();
  const [searchParams] = useSearchParams();
  // One-click join deep link from the waitlist emails (/view-event?id=X&shiftId=Y&action=joinWaitlist):
  // the matching row auto-triggers its current action once mounted — Sign Up when spots are open,
  // Join Waitlist when full (getShiftAction decides; mirrors the action=cancel deep link).
  const oneClickJoin = searchParams.get('action')?.toLowerCase() === 'joinwaitlist';
  const forceJoinWaitlistShiftId = oneClickJoin
    ? (searchParams.get('shiftId') ?? undefined)
    : undefined;
  // One-click leave deep link from the WaitlistJoined email (/view-event?id=X&waitlistEntryId=Y&
  // action=leaveWaitlist): the row holding the matching ACTIVE entry auto-fires its Leave action once
  // mounted. Lands here (not My Activity) so both waitlist CTAs act on the event page, per cce-4614.
  const oneClickLeave = searchParams.get('action')?.toLowerCase() === 'leavewaitlist';
  const forceLeaveWaitlistEntryId = oneClickLeave
    ? (searchParams.get('waitlistEntryId') ?? undefined)
    : undefined;
  // ShiftCarousel surfaced signup/cancel success+error via useSignupAlerts; the table delegates
  // Sign Up / Cancel to the same ShiftActions, so wire the same alert here to avoid a regression.
  const signupAlert = useSignupAlerts(event.eventId);
  // Join/Leave waitlist report success/error at the page level (their controls close without an inline
  // error), mirroring the signup alert above.
  const waitlistAlert = useWaitlistAlerts(event.eventId);

  const shiftsQuery = useAllPagesRedVelvetQuery({
    redVelvetQueryKey: ['getShiftsByEvent', { eventId: event.eventId }],
    // Capacity and the user's row state change server-side via the async auto-signup pipeline (no
    // client mutation to invalidate), so refetch on focus — 'always', since the app-wide
    // staleTime of Infinity means plain `true` would never fire. Same for the signup query below.
    refetchOnWindowFocus: 'always',
    converter: (output: GetShiftsByEventCommandOutput) => ({
      items: output.shifts || [],
      next: output.page,
    }),
  });
  const signupQuery = useAllPagesRedVelvetQuery({
    redVelvetQueryKey: ['getSignupsByQueryParam', { eventId: event.eventId, alias: user.userId }],
    refetchOnWindowFocus: 'always',
    converter: (output: GetSignupsByQueryParamCommandOutput) => ({
      items: output.signups || [],
      next: output.page,
    }),
  });
  const attendanceQuery = useAllPagesRedVelvetQuery({
    redVelvetQueryKey: [
      'getAttendancesByQueryParam',
      { eventId: event.eventId, userAlias: user.userId },
    ],
    converter: (output: GetAttendancesByQueryParamCommandOutput) => ({
      items: output.attendances || [],
      next: output.page,
    }),
  });
  // The user's waitlist entries span all events (getWaitlistsByUser is not event-scoped); we scope
  // them to this event below. useJoinWaitlist already invalidates this query, so joining a shift here
  // reactively updates the row to ON_THE_WAITLIST.
  const waitlistQuery = useGetAllWaitlistsByUser();

  if (
    !shiftsQuery.isFetched ||
    !signupQuery.isFetched ||
    !attendanceQuery.isFetched ||
    !waitlistQuery.isFetched
  ) {
    return <Spinner />;
  }

  const visibleShifts = (shiftsQuery.data || []).filter(
    (shift) =>
      shift.shiftType !== ShiftType.WAITLIST &&
      shift.shiftType !== ShiftType.DROP_IN &&
      shift.name !== 'Waitlist'
  );

  // Scope the user's waitlist entries to this event (matching the event-scoped signups/attendances)
  // and unwrap WaitlistEntryWithEvent -> WaitlistEntry. getUserStatus matches by shiftId, so an
  // ACTIVE entry here resolves the shift's row to ON_THE_WAITLIST + the Leave action.
  const eventWaitlistEntries = (waitlistQuery.data || [])
    .filter((entry) => entry.event.eventId === event.eventId)
    .map((entry) => entry.waitlistEntry);

  return (
    <SpaceBetween size="m">
      {signupAlert.alert && (
        <Alert
          dismissible
          onDismiss={signupAlert.onDismiss}
          type={signupAlert.alert.type}
          i18nStrings={{
            successIconAriaLabel: t(`eventDisplay.${signupAlert.alert.type}Title`),
            dismissAriaLabel: t(`alert.dismiss.${signupAlert.alert.type}`),
          }}
        >
          {signupAlert.alert.message}
        </Alert>
      )}
      {waitlistAlert.alert && (
        <Alert
          dismissible
          onDismiss={waitlistAlert.onDismiss}
          type={waitlistAlert.alert.type}
          i18nStrings={{
            successIconAriaLabel: t(`eventDisplay.${waitlistAlert.alert.type}Title`),
            dismissAriaLabel: t(`alert.dismiss.${waitlistAlert.alert.type}`),
          }}
        >
          {waitlistAlert.alert.message}
        </Alert>
      )}
      <VolunteerEventShiftsTable
        event={event}
        shifts={visibleShifts}
        signups={signupQuery.data || []}
        attendances={attendanceQuery.data || []}
        waitlistEntries={eventWaitlistEntries}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        variant={tableVariant}
        source={source}
        forceJoinWaitlistShiftId={forceJoinWaitlistShiftId}
        forceLeaveWaitlistEntryId={forceLeaveWaitlistEntryId}
        header={
          showTableTitle ? (
            <Header variant="h3" counter={`(${visibleShifts.length})`}>
              {t('volunteerEventShiftsTable.tableTitle')}
            </Header>
          ) : undefined
        }
      />
    </SpaceBetween>
  );
}
